import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { TeamDailyNoteInput } from "@msc/shared";

import {
  useReplaceNotes,
  useTeamDailyNotes,
} from "../../hooks/useTeamDaily.js";

/**
 * Tagesnotizen des Bereichs: kurze Aufgabenliste mit „erledigt"-Haken. Der
 * Client hält den Entwurf lokal und speichert die ganze Liste auf Knopfdruck.
 *
 * **Ein Punkt bleibt stehen, bis er abgehakt ist**, ohne Knopf. Bis zum
 * 2026-09-21 gab es „Offene vom Vortag übernehmen"; wer ihn nicht drückte, sah
 * die offenen Punkte am nächsten Tag nicht mehr. Die Regel liegt jetzt am
 * Server (`teamdailyNotes.service.ts`), der Client schickt dafür die Kennungen
 * mit.
 *
 * **Der Zettel gehört einem Team** (#264, seit 2026-10-01). Vorher hing er am
 * Bereich, und zwei Dailys desselben Bereichs schrieben übereinander. Ohne
 * gewähltes Team gibt es deshalb keinen Zettel, sondern den Hinweis darauf.
 */
export function NotesPanel({
  segmentId,
  teamId,
  date,
}: {
  segmentId: string | null;
  teamId: string | null;
  date: string;
}) {
  const { t } = useTranslation();
  const notesQuery = useTeamDailyNotes(segmentId, teamId, date);
  const replace = useReplaceNotes(segmentId, teamId, date);

  const [draft, setDraft] = useState<TeamDailyNoteInput[]>([]);
  const [dirty, setDirty] = useState(false);

  // Bereich/Tag gewechselt → Entwurf gilt als sauber, damit die frisch geladene
  // Liste übernommen wird.
  useEffect(() => {
    setDirty(false);
  }, [segmentId, teamId, date]);

  // Serverstand übernehmen, solange nichts Ungespeichertes im Entwurf steht.
  useEffect(() => {
    if (notesQuery.data && !dirty) {
      setDraft(
        notesQuery.data.map((n) => ({ id: n.id, text: n.text, done: n.done })),
      );
    }
  }, [notesQuery.data, dirty]);

  const update = (next: TeamDailyNoteInput[]) => {
    setDraft(next);
    setDirty(true);
  };

  const onSave = () => {
    const clean = draft
      .map((n) => ({ id: n.id, text: n.text.trim(), done: n.done }))
      .filter((n) => n.text.length > 0);
    replace.mutate(clean, {
      onSuccess: () => {
        setDirty(false);
        notifications.show({ message: t("common.savedToast") });
      },
      onError: (err) => {
        notifications.show({
          color: "red",
          message: (err as Error).message,
        });
      },
    });
  };

  return (
    <Paper withBorder p="sm" style={{ height: "100%" }}>
      <Group justify="space-between" mb="xs" gap="xs" wrap="nowrap">
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          {t("pages.teamDaily.notes")}
        </Text>
        <Group gap="xs">
          <Button
            size="xs"
            onClick={onSave}
            loading={replace.isPending}
            disabled={!dirty || !segmentId || !teamId}
          >
            {t("common.save")}
          </Button>
        </Group>
      </Group>

      {!teamId && (
        <Text size="sm" c="dimmed">
          {t("pages.teamDaily.notesNoTeam")}
        </Text>
      )}

      <Stack gap="xs">
        {teamId &&
          draft.map((note, index) => (
          <Group key={index} gap="xs" wrap="nowrap">
            <Checkbox
              checked={note.done}
              onChange={(e) => {
                const next = [...draft];
                next[index] = { ...note, done: e.currentTarget.checked };
                update(next);
              }}
            />
            <TextInput
              value={note.text}
              placeholder={t("pages.teamDaily.notePlaceholder")}
              style={{ flex: 1 }}
              styles={{
                input: note.done
                  ? { textDecoration: "line-through", opacity: 0.6 }
                  : undefined,
              }}
              onChange={(e) => {
                const next = [...draft];
                next[index] = { ...note, text: e.currentTarget.value };
                update(next);
              }}
            />
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={t("common.delete")}
              onClick={() => update(draft.filter((_, i) => i !== index))}
            >
              ×
            </ActionIcon>
            </Group>
          ))}
        {teamId && draft.length === 0 && (
          <Text size="sm" c="dimmed">
            {t("pages.teamDaily.notesEmpty")}
          </Text>
        )}
        {teamId && (
          <Button
            size="xs"
            variant="subtle"
            style={{ alignSelf: "flex-start" }}
            onClick={() => update([...draft, { text: "", done: false }])}
          >
            {t("pages.teamDaily.addNote")}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
